import React, { useCallback, useEffect, useRef, useState } from 'react'
import Navigation from './Router'
import ReactPixel from 'react-facebook-pixel'
import { Amplify } from 'aws-amplify'
import { Hub } from 'aws-amplify/utils'
import { signOut } from '@aws-amplify/auth'
import ApiService, { AuthTokenMissingError, setAuthFailureHandler } from './ApiService'
import useUserStore from './hook/useUserStore'
import { deleteAmplifyCookies } from './util/UtilFunctions'
import { getLastActiveTime, setLastActiveTime, clearAuthSessionState, SESSION_TIMEOUT_MS } from './util/tokenStorage'
import { clearCachedTokens, decodeToken, getAccessToken } from './util/authUtil'

ReactPixel.init('155837033166212')
ReactPixel.pageView()

const App = () => {
  const [loading, setLoading] = useState(false)
  const setPatient = useUserStore(state => state.setPatient)
  const isAuth = useUserStore(state => state.isAuth)
  const setIsAuth = useUserStore(state => state.setIsAuth)
  const isLoggingOutRef = useRef(false)

  const wasAuthRef = useRef(false)

  // Single logout path for EVERY teardown trigger: the 401 interceptor, the
  // missing-token handler, an isAuth true->false transition, and idle timeout.
  // Best-effort server-side revoke (signOut) followed by local cleanup +
  // redirect. Guarded by isLoggingOutRef so concurrent triggers run it once,
  // and runs regardless of whether isAuth was previously true — so a
  // missing-token failure on initial load still tears down fully instead of
  // leaving a stale shell. Calling signOut here (not just clearing cookies)
  // prevents Amplify from silently rehydrating the session after a 401.
  const performForcedLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true
    try {
      await signOut({ global: true })
    } catch (err) {
      // ignore signOut errors so cleanup still runs
    }
    deleteAmplifyCookies()
    clearAuthSessionState()
    clearCachedTokens()
    setPatient(null)
    setIsAuth(false)
    if (window.location.pathname !== '/login') {
      window.location.replace('/login')
    }
  }, [setIsAuth, setPatient])

  useEffect(() => {
    setAuthFailureHandler(() => performForcedLogout())
  }, [performForcedLogout])

  useEffect(() => {
    if (wasAuthRef.current && isAuth === false) {
      performForcedLogout()
    }
    wasAuthRef.current = isAuth === true
  }, [isAuth, performForcedLogout])

  useEffect(() => {
    setLoading(true)
    const checkUserSignedIn = async () => {
      try {
        const accessToken = await getAccessToken()
        const decoded = decodeToken(accessToken)
        if (accessToken && decoded) {
          setLastActiveTime(new Date().toISOString())
          setIsAuth(true)
        } else {
          setIsAuth(false)
        }
      } catch (error) {
        setIsAuth(false)
      } finally {
        setLoading(false)
      }
    }
    checkUserSignedIn()
  }, [])

  useEffect(() => {
    if (isAuth !== true) {
      setLoading(false)
      return
    }

    setLoading(true)

    const getPatient = async () => {
      try {
        try {
          const accessToken = await getAccessToken()
          const decoded = decodeToken(accessToken)
          if (!accessToken || !decoded) {
            setIsAuth(false)
            setLoading(false)
            return
          }
          setLastActiveTime(new Date().toISOString())
        } catch {
          setIsAuth(false)
          setLoading(false)
          return
        }
        const res = await ApiService.getPatientAfterSessRef()
        setPatient(res.data)
      } catch (err) {
        setPatient(null)
        if (err instanceof AuthTokenMissingError) {
          setIsAuth(false)
        }
      } finally {
        setLoading(false)
      }
    }

    getPatient()
  }, [isAuth])

  useEffect(() => {
    const listener = data => {
      switch (data.payload.event) {
        case 'signIn':
          if (isAuth !== true) setIsAuth(true)
          break
        case 'signOut':
          setIsAuth(false)
          break
        default:
        // do nothing
      }
    }

    const unsubscribe = Hub.listen('auth', listener)

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!isAuth) return
    isLoggingOutRef.current = false

    let lastWriteTs = 0
    const THROTTLE_MS = 1000
    const updateActivity = () => {
      const now = Date.now()
      if (now - lastWriteTs < THROTTLE_MS) return
      lastWriteTs = now
      setLastActiveTime(new Date().toISOString())
    }

    const interval = setInterval(() => {
      const lastActive = getLastActiveTime()
      if (!lastActive) return
      const lastActiveMs = new Date(lastActive).getTime()
      if (Number.isNaN(lastActiveMs)) return
      if (Date.now() - lastActiveMs > SESSION_TIMEOUT_MS) {
        performForcedLogout()
      }
    }, 60 * 1000)

    const events = ['click', 'keydown', 'mousemove', 'mousedown', 'scroll', 'touchstart']
    events.forEach(evt => window.addEventListener(evt, updateActivity))

    updateActivity()

    return () => {
      clearInterval(interval)
      events.forEach(evt => window.removeEventListener(evt, updateActivity))
    }
  }, [isAuth, performForcedLogout])

  return (
    <div className="App">
      <Navigation loginStatus={isAuth} />
    </div>
  )
}

export default App
